import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  ContactInput,
  FileFolder,
  FileUpdate,
  Lease,
  CloseLease,
  LeaseCreate,
  LeaseTermsUpdate,
  PropertyEvent,
  TerminateLease,
  Occupant,
  OccupantInput,
  PropertyContact,
  StoredFile,
} from '@vuokramani/shared';
import { api, ApiError } from './api.ts';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

// ---------------------------------------------------------------------------
// Vuokrasuhteet
// ---------------------------------------------------------------------------

export function useLeases(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['leases', propertyId],
    queryFn: () =>
      api<{ leases: Lease[] }>(`/properties/${propertyId}/leases`).then((r) => r.leases),
    enabled: Boolean(propertyId),
  });
}

/** Olemassa olevan, paperilla allekirjoitetun vuokrasuhteen kirjaus. */
export function useCreateLease(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaseCreate) =>
      api<Lease>(`/properties/${propertyId}/leases`, { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leases', propertyId] });
      // Asuntolistan "Vuokrattu" ja yhteenveto
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
    },
  });
}

/** Päättämisen muutokset päivittävät vuokrasuhteen, asuntolistan, tarkastukset ja viestit. */
function invalidateLease(queryClient: QueryClient, propertyId: string) {
  for (const key of [
    ['leases', propertyId],
    ['tasks'],
    ['history'],
    ['invoices', propertyId],
    ['properties'],
    ['property', propertyId],
    ['inspections'],
    ['threads'],
    ['threadCounts'],
    ['events', propertyId],
  ]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/** Irtisanominen: viimeinen päivä, viesti vuokralaisille, lopputarkastus avautuu. */
export function useTerminateLease(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TerminateLease) =>
      api<Lease>(`/leases/${leaseId}/terminate`, { method: 'POST', body }),
    onSuccess: () => invalidateLease(queryClient, propertyId),
  });
}

export function useCancelTermination(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>(`/leases/${leaseId}/cancel-termination`, { method: 'POST' }),
    onSuccess: () => invalidateLease(queryClient, propertyId),
  });
}

/** Ehtojen muutos voimassa olevaan vuokrasuhteeseen. Perustelu tallentuu lokiin. */
export function useUpdateLeaseTerms(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaseTermsUpdate) =>
      api<Lease>(`/leases/${leaseId}/terms`, { method: 'PUT', body }),
    onSuccess: () => invalidateLease(queryClient, propertyId),
  });
}

// ---------------------------------------------------------------------------
// Asunnon loki
// ---------------------------------------------------------------------------

/** Lokin kirjaukset uusimmasta alkaen, 200 kerrallaan. */
export function useEvents(propertyId: string) {
  return useInfiniteQuery({
    queryKey: ['events', propertyId],
    queryFn: ({ pageParam }) =>
      api<{ events: PropertyEvent[]; more: boolean }>(
        `/properties/${propertyId}/events${
          pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''
        }`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => (last.more ? last.events.at(-1)?.createdAt : undefined),
    // Loki täydentyy kaikesta, mitä asunnossa tapahtuu: haetaan aina välilehdelle tultaessa.
    staleTime: 0,
  });
}

/** Vakuus palautettu → vuokrasuhde päättyy. */
export function useCloseLease(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CloseLease) =>
      api<void>(`/leases/${leaseId}/close`, { method: 'POST', body }),
    onSuccess: () => invalidateLease(queryClient, propertyId),
  });
}

/** Vuokralaisen kutsu sovellukseen (myös uudelleen). Sähköposti, jos sitä ei ollut. */
export function useInviteParty(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partyId, email }: { partyId: string; email?: string }) =>
      api<void>(`/lease-parties/${partyId}/invite`, {
        method: 'POST',
        body: email ? { email } : {},
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leases', propertyId] }),
  });
}

/**
 * Nykyinen vuokrasuhde: voimassa olevista viimeksi alkanut. Edellinen voi olla vielä
 * voimassa (irtisanottu, vakuus palauttamatta), kun uusi on jo kirjattu.
 */
export function activeLease(leases: Lease[] | undefined): Lease | undefined {
  return leases
    ?.filter((l) => l.status === 'active')
    .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
}

/** Edellinen, irtisanottu vuokrasuhde, jonka vakuus on palauttamatta, kun uusi on jo alkanut. */
export function closingLease(leases: Lease[] | undefined): Lease | undefined {
  const current = activeLease(leases);
  return leases?.find(
    (l) => l.status === 'active' && l.id !== current?.id && l.terminatedBy !== null,
  );
}

/** Vuokrasuhteen sopimustiedosto (toimii myös päättymisen jälkeen säilytysajan). */
export const contractUrl = (leaseId: string) => `${BASE}/leases/${leaseId}/contract`;

/** Sopimustiedoston liitos vuokrasuhteeseen (Sopimukset-kansiosta). */
export function useSetLeaseContract(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string | null) =>
      api<void>(`/leases/${leaseId}/contract`, { method: 'PUT', body: { fileId } }),
    onSuccess: () => {
      invalidateLease(queryClient, propertyId);
      void queryClient.invalidateQueries({ queryKey: ['files', propertyId] });
    },
  });
}

/** Vakuuden palautusselvitys PDF:nä (päättynyt vuokrasuhde). */
export const depositPdfUrl = (leaseId: string) => `${BASE}/leases/${leaseId}/deposit.pdf`;

/** Viimeksi päättynyt vuokrasuhde (myöhäisin päättymispäivä). */
export function lastEndedLease(leases: Lease[] | undefined): Lease | undefined {
  return leases
    ?.filter((l) => l.status === 'ended')
    .sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''))[0];
}

// ---------------------------------------------------------------------------
// Asukkaat ja lemmikit
// ---------------------------------------------------------------------------

export function useOccupants(leaseId: string | undefined) {
  return useQuery({
    queryKey: ['occupants', leaseId],
    queryFn: () =>
      api<{ occupants: Occupant[] }>(`/leases/${leaseId}/occupants`).then((r) => r.occupants),
    enabled: Boolean(leaseId),
  });
}

export function useAddOccupant(leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OccupantInput) =>
      api<Occupant>(`/leases/${leaseId}/occupants`, { method: 'POST', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['occupants', leaseId] }),
  });
}

/** Lemmikit sallittu vai ei (vuokranantaja). Päivittää vuokrasuhteen tiedot. */
export function useSetPetsAllowed(leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (allowed: boolean) =>
      api<void>(`/leases/${leaseId}/pets`, { method: 'PUT', body: { allowed } }),
    // Odotetaan päivitetty vuokrasuhde, jotta valinta ei välähdä takaisin vanhaan.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leases'] }),
  });
}

export function useDeleteOccupant(leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/occupants/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['occupants', leaseId] }),
  });
}

// ---------------------------------------------------------------------------
// Taloyhtiön yhteystiedot
// ---------------------------------------------------------------------------

export function useContacts(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['contacts', propertyId],
    queryFn: () =>
      api<{ contacts: PropertyContact[] }>(`/properties/${propertyId}/contacts`).then(
        (r) => r.contacts,
      ),
    enabled: Boolean(propertyId),
  });
}

export function useSaveContact(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: ContactInput }) =>
      id
        ? api<PropertyContact>(`/contacts/${id}`, { method: 'PUT', body })
        : api<PropertyContact>(`/properties/${propertyId}/contacts`, { method: 'POST', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', propertyId] }),
  });
}

export function useDeleteContact(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', propertyId] }),
  });
}

// ---------------------------------------------------------------------------
// Tiedostot
// ---------------------------------------------------------------------------

/** Tiedostot näkyvät myös huoltokirjan liitteinä, joten molemmat päivitetään. */
function invalidateFiles(queryClient: QueryClient, propertyId: string) {
  void queryClient.invalidateQueries({ queryKey: ['files', propertyId] });
  void queryClient.invalidateQueries({ queryKey: ['maintenance', propertyId] });
}

export function useFiles(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['files', propertyId],
    queryFn: () =>
      api<{ files: StoredFile[] }>(`/properties/${propertyId}/files`).then((r) => r.files),
    enabled: Boolean(propertyId),
  });
}

/** Tiedoston osoite: avautuu selaimessa, tai download = tallennetaan. */
export function fileUrl(id: string, download = false): string {
  return `${BASE}/files/${id}/content${download ? '?download=1' : ''}`;
}

export function isImage(file: Pick<StoredFile, 'mimeType'>): boolean {
  return file.mimeType.startsWith('image/');
}

export function useUploadFiles(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      files,
      folder,
      shared,
      maintenanceEntryId,
    }: {
      files: File[];
      folder: FileFolder;
      shared: boolean;
      /** Huoltokirjan tapahtuma, jonka liitteiksi tiedostot tulevat. */
      maintenanceEntryId?: string;
    }) => {
      // Kentät ennen tiedostoja: backend lukee ne ensin.
      const form = new FormData();
      form.append('folder', folder);
      form.append('shared', String(shared));
      if (maintenanceEntryId) form.append('maintenanceEntryId', maintenanceEntryId);
      for (const file of files) form.append('file', file, file.name);
      const response = await fetch(`${BASE}/properties/${propertyId}/files`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const data = (await response.json().catch(() => null)) as {
        error?: string;
        message?: string;
        files?: StoredFile[];
      } | null;
      if (!response.ok) {
        throw new ApiError(response.status, data?.error ?? 'server_error', data?.message);
      }
      return data?.files ?? [];
    },
    onSuccess: () => invalidateFiles(queryClient, propertyId),
  });
}

export function useUpdateFile(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FileUpdate }) =>
      api<StoredFile>(`/files/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateFiles(queryClient, propertyId),
  });
}

export function useDeleteFile(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/files/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFiles(queryClient, propertyId),
  });
}

export function formatBytes(bytes: number, locale = 'fi-FI'): string {
  const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return `${nf.format(Math.max(1, Math.round(bytes / 1024)))} kt`;
  return `${nf.format(bytes / (1024 * 1024))} Mt`;
}
